import React from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Button } from "./ui/button";

export function Icon({ name, className }) {
  const Cmp = Icons[name] || Icons.Circle;
  return <Cmp className={className} />;
}

export function Eyebrow({ children, light }) {
  return (
    <span
      className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] ${
        light ? "text-brand-200" : "text-brand-600"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${light ? "bg-brand-teal" : "bg-brand-teal"}`} />
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, center, light }) {
  return (
    <div className={`${center ? "mx-auto text-center" : ""} max-w-2xl`}>
      {eyebrow && <Eyebrow light={light}>{eyebrow}</Eyebrow>}
      <h2
        className={`mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-balance ${
          light ? "text-white" : "text-brand-navy"
        }`}
      >
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base sm:text-lg leading-relaxed ${light ? "text-brand-100/90" : "text-slate-600"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

export function FeatureCard({ icon, title, body }) {
  return (
    <div className="group relative rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand-200 hover:shadow-xl hover:shadow-brand-500/10">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-100 transition-colors group-hover:bg-brand-600 group-hover:text-white">
        <Icon name={icon} className="h-6 w-6" />
      </div>
      <h3 className="mt-5 text-lg font-semibold text-brand-navy">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
    </div>
  );
}

export function StatCard({ value, label, icon, light }) {
  return (
    <div
      className={`rounded-2xl border p-6 text-center ${
        light ? "border-white/10 bg-white/5 backdrop-blur" : "border-slate-200 bg-white shadow-sm"
      }`}
    >
      {icon && (
        <div className={`mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl ${light ? "bg-brand-teal/15 text-brand-teal" : "bg-brand-50 text-brand-600"}`}>
          <Icon name={icon} className="h-5 w-5" />
        </div>
      )}
      <div className={`text-2xl sm:text-3xl font-bold font-display ${light ? "text-white" : "text-brand-600"}`}>{value}</div>
      <div className={`mt-1 text-sm ${light ? "text-brand-100/80" : "text-slate-500"}`}>{label}</div>
    </div>
  );
}

export function PageHero({ eyebrow, title, subtitle, image, children }) {
  return (
    <section className="relative overflow-hidden bg-brand-navy">
      <div className="absolute inset-0 grid-dots opacity-30" />
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-600/30 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-brand-teal/20 blur-3xl" />
      <div className="container-x relative py-16 sm:py-20 lg:py-24">
        <div className={`grid items-center gap-10 ${image ? "lg:grid-cols-2" : ""}`}>
          <div className="animate-fade-up">
            <Eyebrow light>{eyebrow}</Eyebrow>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance">{title}</h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-brand-100/90">{subtitle}</p>
            {children}
          </div>
          {image && (
            <div className="relative animate-fade-up">
              <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
                <img src={image} alt={title} className="h-full w-full object-cover" loading="lazy" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function CTABand({ title, body, primaryTo = "/contact-us", primaryLabel = "Get a Free Revenue Risk Audit", secondaryTo, secondaryLabel }) {
  return (
    <section className="container-x py-16 sm:py-20">
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 px-6 py-12 sm:px-12 sm:py-16 text-center shadow-xl">
        <div className="absolute inset-0 grid-dots opacity-20" />
        <div className="relative mx-auto max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-balance">{title}</h2>
          {body && <p className="mt-4 text-lg text-brand-100/90">{body}</p>}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button asChild size="lg" className="bg-white text-brand-700 hover:bg-brand-50 w-full sm:w-auto">
              <Link to={primaryTo}>{primaryLabel}</Link>
            </Button>
            {secondaryTo && (
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white">
                <Link to={secondaryTo}>{secondaryLabel}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function CheckList({ items, light }) {
  return (
    <ul className="space-y-3">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-3">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-teal/15 text-brand-teal">
            <Icons.Check className="h-4 w-4" />
          </span>
          <span className={`text-sm leading-relaxed ${light ? "text-brand-100/90" : "text-slate-700"}`}>{item}</span>
        </li>
      ))}
    </ul>
  );
}
